// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import { type ReactNode } from "react";

import { useReadingMessage } from "./useReadingMessage";

import { useAutoScroll } from "../../utils/useAutoScroll";
import { TextSelectionToolbar } from "../transport/TextSelectionToolbar";

type AskV2ConversationShellProps = {
  testId: string;
  header?: ReactNode;
  tabs?: ReactNode;
  /** Omitted when `fullBleed` — composer lives inside the review workspace left column. */
  composer?: ReactNode;
  /** Review workspace fills the content column edge-to-edge (no 720px cap). */
  fullBleed?: boolean;
  rail?: ReactNode;
  children: ReactNode;
  /** Omitted when `fullBleed` — review feed mounts its own TextSelectionToolbar. */
  onAddQuotedContext?: (input: {
    text: string;
    sourceMessageId?: string;
  }) => void;
  /** When set with scrollDeps, pins the thread to the latest messages on load. */
  scrollResetKey?: unknown;
  scrollDeps?: readonly unknown[];
  autoScrollEnabled?: boolean;
  /**
   * See `UseAutoScrollOptions.sentSignal` — a local send outranks the hold.
   * Every consumer that renders a send-capable `composer` must pass this
   * (`transport.sending`). Omitting it leaves the sent row below the fold for
   * any reader who had scrolled up: the sticky-up guard is still latched when
   * the optimistic row renders and nothing else in ask-v2 clears it on send.
   * (The `autoScrollEnabled` hold compounds it once `inFlight` catches up, but
   * it is not the blocker on the commit that first paints the row.) All three
   * consumers do: `AskV2ChatPage`, `AskV2ProjectPage`, and `AskV2Home`'s
   * pre-navigate thread — Home matters most, because that shell mounts inside
   * the pre-ACK window with `sending` already true, so the mount itself is the
   * rising edge that arms the pin. A new consumer that wires `scrollDeps` +
   * `autoScrollEnabled` off `transport.inFlight` and skips this prop is the
   * one bug this doc exists to prevent.
   */
  scrollSentSignal?: boolean;
  /** The answer the reader scrolls to, or null at the bottom (`useReadingMessage`). */
  onReadingMessageChange?: (messageId: string | null) => void;
};

export function AskV2ConversationShell({
  testId,
  header,
  tabs,
  composer,
  fullBleed = false,
  rail,
  children,
  onAddQuotedContext,
  scrollResetKey,
  scrollDeps,
  autoScrollEnabled = true,
  scrollSentSignal,
  onReadingMessageChange,
}: AskV2ConversationShellProps) {
  const autoScrollActive = scrollDeps != null;
  const { scrollContainerRef, contentRef, handleScroll } = useAutoScroll(
    scrollDeps ? [...scrollDeps] : [],
    scrollResetKey,
    {
      enabled: autoScrollActive && autoScrollEnabled,
      sentSignal: scrollSentSignal,
    },
  );
  useReadingMessage(scrollContainerRef, onReadingMessageChange);

  return (
    <div
      data-testid={testId}
      className="ask-puntt-surface relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
    >
      {header}
      {tabs}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {fullBleed ? (
            <div
              className="relative flex min-h-0 flex-1 overflow-hidden"
              data-testid="ask-v2-messages"
            >
              {children}
            </div>
          ) : (
            <>
              <div
                ref={scrollContainerRef}
                onScroll={autoScrollActive ? handleScroll : undefined}
                className="relative flex-1 overflow-y-auto px-4 py-6"
                data-testid="ask-v2-messages"
              >
                {onAddQuotedContext ? (
                  <TextSelectionToolbar
                    containerRef={scrollContainerRef}
                    onAddContext={onAddQuotedContext}
                  />
                ) : null}
                <div
                  ref={contentRef}
                  className="mx-auto w-full max-w-[720px] space-y-4"
                >
                  {children}
                </div>
              </div>
              {composer ? (
                <div
                  className="shrink-0 p-4"
                  data-testid="ask-v2-composer-region"
                >
                  <div className="mx-auto w-full max-w-[720px]">{composer}</div>
                </div>
              ) : null}
            </>
          )}
        </div>
        {rail}
      </div>
    </div>
  );
}
